import type {
  JobResponse,
  SubtitleEditorDocument,
  SpeakerBinding,
  UserProfile,
  BatchCreateResult,
  BatchResponse,
  BatchSummary,
  BillingCycle,
} from "./types";

export const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:8080";

// jobAssetUrl 拼出任务产物的直链（封面/原片/成片等）。这些路由无需鉴权，
// 可直接用于 <img src> / <a href>，只是数据以 job id 为键。
export function jobAssetUrl(jobId: string, asset: "input-poster" | "poster" | "output-video" | "output-subtitle"): string {
  return `${API_BASE_URL}/jobs/${jobId}/${asset}`;
}

export interface SpeakerAnalysis {
  id: string;
  gender: string;
  suggested_voice: string;
  sample_line?: string;
  segment_count?: number;
  /** 该说话人的一段原声 mp3 URL，点击即可听到本人真实声音 */
  sample_audio_url?: string;
  /** 发声时长占比（0~1），体现主次 */
  duration_share?: number;
}

export interface AnalysisResult {
  analysis_id: string;
  duration_seconds: number;
  speakers: SpeakerAnalysis[];
}

export async function analyzeVideo(
  file: File | null,
  token: string,
  onProgress?: (pct: number) => void,
  uploadKey?: string,
  targetLang?: string,
): Promise<AnalysisResult> {
  const formData = new FormData();
  if (uploadKey) {
    formData.append("upload_key", uploadKey);
  } else if (file) {
    formData.append("file", file);
  } else {
    throw new Error("缺少文件或上传密钥");
  }
  if (targetLang) {
    formData.append("target_lang", targetLang);
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE_URL}/jobs/analyze`);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    xhr.upload.onprogress = (e: ProgressEvent) => {
      if (!e.lengthComputable || !onProgress) return;
      const pct = Math.round((e.loaded / e.total) * 100);
      onProgress(Math.min(99, pct));
    };

    xhr.onload = () => {
      if (xhr.status === 401) {
        reject(new Error("请先登录后再分析"));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        let msg = `分析失败（${xhr.status}）`;
        try {
          const parsed = JSON.parse(xhr.responseText);
          if (parsed?.error) msg = parsed.error;
        } catch {
          /* ignore */
        }
        reject(new Error(msg));
        return;
      }
      try {
        resolve(JSON.parse(xhr.responseText) as AnalysisResult);
      } catch (e) {
        reject(new Error("解析响应失败"));
      }
    };

    xhr.onerror = () => reject(new Error("网络错误，请重试"));
    xhr.send(formData);
  });
}

export async function createJob(
  file: File | null,
  targetLang: string,
  includeSubtitles: boolean,
  keepSourceSubtitles: boolean,
  options: {
    genTitle?: boolean;
    genHashtags?: boolean;
    ttsVoice?: string;
    token?: string;
    onProgress?: (pct: number) => void;
    uploadKey?: string;
    speakerVoices?: Record<string, string>;
    speakerLabels?: Record<string, string>;
    speakerClonedVoices?: Record<string, string>;
    diarize?: boolean;
    reviewBeforeRender?: boolean;
    analysisId?: string;
    useSpeakerVoiceClone?: boolean;
    subtitleKey?: string;
    subtitleLang?: string;
  } = {}
): Promise<{ job_id: string; job_token?: string; status: string }> {
  const formData = new FormData();
  if (options.uploadKey) {
    formData.append("upload_key", options.uploadKey);
  } else if (file) {
    formData.append("file", file);
  } else {
    throw new Error("缺少文件或上传密钥");
  }
  formData.append("target_lang", targetLang);
  formData.append("include_subtitles", String(includeSubtitles));
  formData.append("keep_source_subtitles", String(keepSourceSubtitles));
  formData.append("gen_title", String(options.genTitle ?? false));
  formData.append("gen_hashtags", String(options.genHashtags ?? false));
  if (options.ttsVoice) {
    formData.append("tts_voice", options.ttsVoice);
  }

  // 两段式开关：默认开启（勾选=翻译后暂停校对文稿）；缺省视为 true 与后端一致
  formData.append("review_before_render", String(options.reviewBeforeRender ?? true));
  if (options.diarize) {
    formData.append("diarize", "true");
  }
  if (options.speakerVoices && Object.keys(options.speakerVoices).length > 0) {
    formData.append("speaker_voices", JSON.stringify(options.speakerVoices));
  }
  if (options.speakerLabels && Object.keys(options.speakerLabels).length > 0) {
    formData.append("speaker_labels", JSON.stringify(options.speakerLabels));
  }
  if (options.speakerClonedVoices && Object.keys(options.speakerClonedVoices).length > 0) {
    formData.append("speaker_cloned_voices", JSON.stringify(options.speakerClonedVoices));
  }
  if (options.analysisId) {
    // 带上分析阶段 ID：后端复用同一份说话人分离结果，保证成片人数与确认页一致。
    formData.append("analysis_id", options.analysisId);
  }
  if (options.useSpeakerVoiceClone) {
    formData.append("use_speaker_voice_clone", "true");
  }
  if (options.subtitleKey) {
    formData.append("subtitle_key", options.subtitleKey);
  }
  if (options.subtitleLang) {
    formData.append("subtitle_lang", options.subtitleLang);
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE_URL}/jobs`);
    if (options.token) {
      xhr.setRequestHeader("Authorization", `Bearer ${options.token}`);
    }

    xhr.upload.onprogress = (e: ProgressEvent) => {
      if (!e.lengthComputable || !options.onProgress) return;
      const pct = Math.round((e.loaded / e.total) * 100);
      options.onProgress(Math.min(99, pct));
    };

    xhr.onload = () => {
      if (xhr.status === 401) {
        reject(new Error("请先登录后再创建任务"));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        let msg = `创建任务失败（${xhr.status}）`;
        try {
          const parsed = JSON.parse(xhr.responseText);
          if (parsed?.error) msg = parsed.error;
        } catch {
          /* ignore */
        }
        reject(new Error(msg));
        return;
      }
      try {
        resolve(JSON.parse(xhr.responseText) as { job_id: string; job_token?: string; status: string });
      } catch {
        reject(new Error("响应解析失败"));
      }
    };

    xhr.onerror = () => reject(new Error("网络错误，上传失败"));
    xhr.send(formData);
  });
}

export async function fetchJob(
  jobId: string,
  options: { token?: string; jobToken?: string } = {}
): Promise<JobResponse> {
  const headers: Record<string, string> = {};
  if (options.token) {
    headers["Authorization"] = `Bearer ${options.token}`;
  }
  if (options.jobToken) {
    headers["X-Job-Token"] = options.jobToken;
  }

  const url = new URL(`${API_BASE_URL}/jobs/${jobId}`);
  if (options.jobToken) {
    url.searchParams.set("t", options.jobToken);
  }

  const response = await fetch(url.toString(), {
    cache: "no-store",
    headers,
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? "查询任务失败");
  }

  return (await response.json()) as JobResponse;
}

export async function fetchUserJobs(token: string): Promise<any[]> {
  const response = await fetch(`${API_BASE_URL}/jobs`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error("同步云端任务失败");
  }
  return (await response.json()) as any[];
}

export interface JobPageResult {
  items: any[];
  total: number;
  page: number;
  page_size: number;
  /** 单任务允许的手动重试上限（后端 config.MaxJobRetries），用于判断失败项是否还能重试 */
  max_job_retries?: number;
}

// 服务端分页拉取作品列表（登录态）。支持状态过滤、文件名搜索与创建日期区间。
// from / to 为 YYYY-MM-DD，含首含尾。
export async function fetchUserJobsPage(
  token: string,
  params: {
    page?: number;
    page_size?: number;
    status?: string;
    query?: string;
    from?: string;
    to?: string;
  } = {},
): Promise<JobPageResult> {
  const url = new URL(`${API_BASE_URL}/jobs`);
  url.searchParams.set("page", String(params.page ?? 1));
  url.searchParams.set("page_size", String(params.page_size ?? 20));
  if (params.status) url.searchParams.set("status", params.status);
  if (params.query) url.searchParams.set("query", params.query);
  if (params.from) url.searchParams.set("from", params.from);
  if (params.to) url.searchParams.set("to", params.to);

  const response = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
    cache: "no-store",
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? "同步云端任务失败");
  }
  return (await response.json()) as JobPageResult;
}

export async function retryJob(jobId: string, token: string): Promise<{ job_id: string; status: string }> {
  const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/retry`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? "重试任务失败");
  }

  return (await response.json()) as { job_id: string; status: string };
}

// 删除作品（任务）。后端拒绝删除「处理中」的任务，调用方需自行兜底提示。
export async function deleteJob(jobId: string, token: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/jobs/${jobId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    const raw = payload?.error ?? "";
    // 后端错误是英文短句，转成用户可读的中文提示
    if (raw.includes("processing job cannot be deleted")) {
      throw new Error("作品正在处理中，需等它跑完或失败后才能删除");
    }
    throw new Error(raw || "删除作品失败");
  }
}

export async function fetchUserMe(token: string): Promise<UserProfile> {
  const response = await fetch(`${API_BASE_URL}/user/me`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? "获取用户信息失败");
  }
  return (await response.json()) as UserProfile;
}

export async function fetchEditorDocument(jobId: string): Promise<SubtitleEditorDocument> {
  const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/editor-data`, {
    cache: "no-store",
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? "获取字幕编辑数据失败");
  }

  return (await response.json()) as SubtitleEditorDocument;
}

export async function saveEditorDocument(
  jobId: string,
  updates: Array<{
    id: string;
    start: number;
    end: number;
    final_text: string;
    speaker?: string;
    /** 该句配音变速倍率（>=1 只加快），后端在重生成配音时以 atempo 应用 */
    speed?: number;
  }>,
  speakers?: SpeakerBinding[],
): Promise<SubtitleEditorDocument> {
  const body: { segments: typeof updates; speakers?: SpeakerBinding[] } = { segments: updates };
  if (speakers) {
    body.speakers = speakers;
  }
  const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/editor-data`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? "保存字幕编辑失败");
  }

  return (await response.json()) as SubtitleEditorDocument;
}

export async function renderFinalVideo(jobId: string): Promise<JobResponse> {
  const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/render-final`, {
    method: "POST",
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? "重新生成最终视频失败");
  }

  return (await response.json()) as JobResponse;
}

export async function createBatch(
  files: File[],
  targetLang: string,
  includeSubtitles: boolean,
  keepSourceSubtitles: boolean,
  token: string,
  options: {
    genTitle?: boolean;
    genHashtags?: boolean;
    ttsVoice?: string;
    onProgress?: (pct: number) => void;
    uploadKeys?: string[];
    reviewBeforeRender?: boolean;
    subtitleKey?: string;
    subtitleLang?: string;
  } = {}
): Promise<BatchCreateResult> {
  const formData = new FormData();
  for (const k of options.uploadKeys ?? []) {
    formData.append("upload_key", k);
  }
  for (const f of files) {
    formData.append("file", f);
  }
  formData.append("target_lang", targetLang);
  formData.append("include_subtitles", String(includeSubtitles));
  formData.append("keep_source_subtitles", String(keepSourceSubtitles));
  formData.append("gen_title", String(options.genTitle ?? false));
  formData.append("gen_hashtags", String(options.genHashtags ?? false));
  if (options.ttsVoice) {
    formData.append("tts_voice", options.ttsVoice);
  }

  if (options.subtitleKey) {
    formData.append("subtitle_key", options.subtitleKey);
  }
  if (options.subtitleLang) {
    formData.append("subtitle_lang", options.subtitleLang);
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE_URL}/jobs/batch`);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    xhr.upload.onprogress = (e: ProgressEvent) => {
      if (!e.lengthComputable || !options.onProgress) return;
      const pct = Math.round((e.loaded / e.total) * 100);
      options.onProgress(Math.min(99, pct));
    };

    xhr.onload = () => {
      if (xhr.status === 401) {
        reject(new Error("请先登录后再创建任务"));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        let msg = `批量创建任务失败（${xhr.status}）`;
        try {
          const parsed = JSON.parse(xhr.responseText);
          if (parsed?.error) msg = parsed.error;
        } catch {
          /* ignore */
        }
        reject(new Error(msg));
        return;
      }
      try {
        resolve(JSON.parse(xhr.responseText) as BatchCreateResult);
      } catch {
        reject(new Error("响应解析失败"));
      }
    };

    xhr.onerror = () => reject(new Error("网络错误，上传失败"));
    xhr.send(formData);
  });
}

export async function fetchBatch(batchId: string, token: string): Promise<BatchResponse> {
  const response = await fetch(`${API_BASE_URL}/batches/${batchId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    cache: "no-store",
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? "查询批次失败");
  }
  return (await response.json()) as BatchResponse;
}

export async function fetchBatches(token: string): Promise<BatchSummary[]> {
  const response = await fetch(`${API_BASE_URL}/batches`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    cache: "no-store",
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? "查询批次列表失败");
  }
  const data = (await response.json()) as BatchSummary[];
  return data ?? [];
}

export interface BatchPageResult {
  items: BatchSummary[];
  total: number;
  page: number;
  page_size: number;
}

// 服务端分页拉取批量任务历史。
export async function fetchBatchesPage(
  token: string,
  params: { page?: number; page_size?: number; status?: string; query?: string } = {},
): Promise<BatchPageResult> {
  const url = new URL(`${API_BASE_URL}/batches`);
  url.searchParams.set("page", String(params.page ?? 1));
  url.searchParams.set("page_size", String(params.page_size ?? 20));
  if (params.status) url.searchParams.set("status", params.status);
  if (params.query) url.searchParams.set("query", params.query);

  const response = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
    cache: "no-store",
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? "查询批次列表失败");
  }
  return (await response.json()) as BatchPageResult;
}

// checkQuota 上传前的额度预校验：给定时长 + 目标语言数 + 是否克隆，返回剩余额度是否足够。
// 在把视频传到对象存储之前调用，避免额度不足时仍上传文件、浪费带宽与存储。
// 计费规则与后端 reservePlanQuota 完全一致（多语言按 N 倍、克隆按倍率追加附加分钟）。
export async function checkQuota(
  token: string,
  params: { duration_seconds: number; lang_count: number; clone_used: boolean },
): Promise<{ ok: boolean; remaining: number; required: number; base: number; surcharge: number }> {
  const res = await fetch(`${API_BASE_URL}/jobs/quota-check`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? "额度预校验失败");
  }
  return (await res.json()) as {
    ok: boolean;
    remaining: number;
    required: number;
    base: number;
    surcharge: number;
  };
}

// 直传到对象存储：通过预签名 URL 让浏览器直接 PUT 到 R2，后端不经手原始字节。
// 不再保留服务端接收兜底：直传失败（额度/CORS/网络）直接抛出，前端不做无意义回退。
async function putToURL(
  url: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.upload.onprogress = (e: ProgressEvent) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else {
        let msg = `直传失败（${xhr.status}）`;
        try {
          const parsed = JSON.parse(xhr.responseText);
          if (parsed?.error) msg = parsed.error;
        } catch {
          /* ignore */
        }
        reject(new Error(msg));
      }
    };
    xhr.onerror = () => reject(new Error("网络错误，直传失败"));
    xhr.send(file);
  });
}

// presignPut 向服务端换取预签名 PUT URL 并直传 R2。任何失败（额度/CORS/网络）都直接抛出，
// 不再回退到服务端接收——前端只走直传这一条路径。
async function presignPut(
  file: File,
  token: string,
  onProgress?: (pct: number) => void,
): Promise<{ key: string; public_url: string }> {
  const res = await fetch(`${API_BASE_URL}/upload/presign`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ filename: file.name }),
  });
  if (res.status === 402) {
    const payload = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? "额度不足，无法上传");
  }
  if (!res.ok) {
    let msg = `获取上传凭证失败（${res.status}）`;
    try {
      const parsed = (await res.json().catch(() => null)) as { error?: string } | null;
      if (parsed?.error) msg = parsed.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  const data = (await res.json()) as { key: string; upload_url: string } | null;
  if (!data || !data.upload_url) {
    throw new Error("获取上传凭证失败");
  }
  // 直传 PUT 可能因 R2 未配置 CORS / 网络异常失败：直接抛出，由调用方提示用户检查 R2 配置。
  await putToURL(data.upload_url, file, onProgress);
  return { key: data.key, public_url: "" };
}

// uploadVideo 把视频直传到 R2（预签名 PUT），返回 key。
// 仅在「开始译制」时调用：先暂存一次，再用同一个 key 为每种目标语言建任务，
// 避免多语言场景下把同一个视频重复上传 N 遍。无任何服务端接收兜底。
export async function uploadVideo(
  file: File,
  token: string,
  onProgress?: (pct: number) => void,
): Promise<{ key: string; public_url: string }> {
  return presignPut(file, token, onProgress);
}

// uploadSubtitle 把字幕文件（SRT/VTT/ASS/TXT）直传到 R2（预签名 PUT），返回 key。无服务端接收兜底。
export async function uploadSubtitle(
  file: File,
  token: string,
): Promise<{ key: string; public_url: string }> {
  return presignPut(file, token);
}

export async function loginWithGoogle(idToken: string): Promise<{
  token: string;
  user: UserProfile;
}> {
  const response = await fetch(`${API_BASE_URL}/auth/google`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id_token: idToken }),
  });
  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? "Google 登录失败");
  }
  return (await response.json()) as { token: string; user: UserProfile };
}

export async function createCreemCheckout(
  token: string,
  planKey: string,
  cycle: BillingCycle,
): Promise<{ order_id: string; checkout_url: string }> {
  const response = await fetch(`${API_BASE_URL}/payments/creem/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ plan_key: planKey, cycle }),
  });
  const data = (await response.json().catch(() => null)) as { error?: string } | null;
  if (!response.ok) throw new Error(data?.error ?? "创建结账会话失败");
  return data as { order_id: string; checkout_url: string };
}

export type OrderStatus = "pending" | "paid" | "failed" | string;

export async function fetchOrderStatus(
  token: string,
  orderId: string,
): Promise<{ order_id: string; status: OrderStatus; plan_id: string; paid_at: string | null }> {
  const response = await fetch(
    `${API_BASE_URL}/payments/orders/${encodeURIComponent(orderId)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  const data = (await response.json().catch(() => null)) as { error?: string } | null;
  if (!response.ok) throw new Error(data?.error ?? "查询订单状态失败");
  return data as { order_id: string; status: OrderStatus; plan_id: string; paid_at: string | null };
}

export async function fetchBillingPortal(token: string): Promise<{ url: string }> {
  const response = await fetch(`${API_BASE_URL}/billing/portal`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await response.json().catch(() => null)) as { error?: string } | null;
  if (!response.ok) throw new Error(data?.error ?? "获取账单门户失败");
  return data as { url: string };
}

// ===== 声音克隆（Voice Clone）=====

export interface ClonedVoice {
  id: string;
  user_id: number;
  name: string;
  provider: string;
  provider_voice_id: string;
  consent: boolean;
  sample_r2_key: string;
  status: string; // enrolling | ready | failed
  created_at: string;
}

export interface VoiceCloneCapabilities {
  enabled: boolean;
  providers: string[];
}

export async function fetchVoiceCloneCapabilities(token: string): Promise<VoiceCloneCapabilities> {
  const res = await fetch(`${API_BASE_URL}/voice-clone/capabilities`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("获取声音克隆能力失败");
  return (await res.json()) as VoiceCloneCapabilities;
}

export async function listVoices(token: string): Promise<ClonedVoice[]> {
  const res = await fetch(`${API_BASE_URL}/user/voices`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("获取音色列表失败");
  const data = (await res.json()) as { voices?: ClonedVoice[] };
  return data.voices ?? [];
}

export async function createVoice(
  token: string,
  params: { name: string; provider: string; consent: boolean; file: Blob | File },
): Promise<{ voice: ClonedVoice; provider_voice_id: string }> {
  const form = new FormData();
  form.append("name", params.name);
  form.append("provider", params.provider);
  form.append("consent", String(params.consent));
  form.append("sample", params.file);
  const res = await fetch(`${API_BASE_URL}/user/voices`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? "创建音色失败");
  }
  return (await res.json()) as { voice: ClonedVoice; provider_voice_id: string };
}

export async function deleteVoice(token: string, id: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/user/voices/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? "删除音色失败");
  }
}

export async function renameVoice(token: string, id: string, name: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/user/voices/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? "重命名失败");
  }
}

export async function fetchVoiceSample(token: string, id: string): Promise<string> {
  const res = await fetch(`${API_BASE_URL}/user/voices/${id}/sample`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? "试听合成失败");
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export interface SpeakerCloneResult {
  speaker: string;
  voice_id: string;
  preview_url: string;
  label: string;
  error?: string;
}

// 生成视频原声克隆 + 试听：对视频中每个说话人并行生成克隆音色并合成试听。
// 与编辑器其它端点一致，公开调用（不带 token），按 job 归属确定所有者。
export async function generateSpeakerClones(jobId: string): Promise<{ clones: SpeakerCloneResult[] }> {
  const res = await fetch(`${API_BASE_URL}/jobs/${jobId}/speaker-clones`, { method: "POST" });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? "生成原声克隆失败");
  }
  return (await res.json()) as { clones: SpeakerCloneResult[] };
}
